// TaskModal.jsx - unified create/edit task modal (replaces the separate
// CreateTaskModal.jsx / EditTaskModal.jsx, which are no longer wired into
// TasksDashboard.jsx but are left in place rather than deleted).
import React, { useState, useEffect } from 'react';
import { tasksService } from '../../services';
import { clientsService } from '../../services/clientsService';

const INPUT = 'w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[rgba(196,135,31,0.25)] focus:border-[var(--accent)] transition-all';

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-[var(--muted-foreground)] mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().slice(0, 16);
};

const TaskModal = ({ task, onClose, onSaved }) => {
  const isEdit = !!task;
  const [formData, setFormData] = useState({
    client_id: task?.client_id || '',
    title: task?.title || '',
    description: task?.description || '',
    priority: task?.priority || 'medium',
    status: task?.status || 'pending',
    category: task?.category || '',
    due_date: task?.due_date ? formatDateForInput(task.due_date) : '',
    tags: Array.isArray(task?.tags) ? task.tags.join(', ') : (task?.tags ? JSON.parse(task.tags).join(', ') : ''),
    reminder_enabled: task?.reminder_enabled || false,
    reminder_hours_before: task?.reminder_hours_before || 24,
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    clientsService.getAll().then(setClients).catch(err => {
      console.error('Failed to fetch clients:', err);
      setError('Failed to load clients. Please try again.');
    });
  }, []);

  const upd = (patch) => setFormData(prev => ({ ...prev, ...patch }));
  const valid = formData.client_id && formData.title;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) {
      setError('Client and title are required fields.');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      ...formData,
      due_date: formData.due_date || null,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    };

    try {
      if (isEdit) {
        await tasksService.updateTask(task.id, payload);
      } else {
        await tasksService.createTask(payload);
      }
      onSaved();
    } catch (err) {
      console.error(`Failed to ${isEdit ? 'update' : 'create'} task:`, err);
      setError(err.response?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} task. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15,28,46,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        style={{ animation: 'taskModalFadeUp .2s ease-out' }}>

        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="text-base font-bold text-[var(--foreground)]" style={{ fontFamily: 'var(--font-display)' }}>
            {isEdit ? 'Edit Task' : 'Create New Task'}
          </h2>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-[var(--muted-foreground)] hover:bg-[var(--muted)] transition-all">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
          <div className="p-6 space-y-4">
            {error && (
              <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-medium text-red-700 bg-red-50 border border-red-200">
                {error}
              </div>
            )}

            <Field label="Client" required>
              <select value={formData.client_id} onChange={e => upd({ client_id: e.target.value })} required className={`${INPUT} cursor-pointer`}>
                <option value="">Select a client</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.email})</option>
                ))}
              </select>
            </Field>

            <Field label="Task Title" required>
              <input value={formData.title} onChange={e => upd({ title: e.target.value })} required
                placeholder="Enter task title" className={INPUT} />
            </Field>

            <Field label="Description">
              <textarea value={formData.description} onChange={e => upd({ description: e.target.value })}
                rows={3} placeholder="Enter task description (optional)" className={`${INPUT} resize-none`} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Priority" required>
                <select value={formData.priority} onChange={e => upd({ priority: e.target.value })} className={`${INPUT} cursor-pointer`}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </Field>
              {isEdit ? (
                <Field label="Status" required>
                  <select value={formData.status} onChange={e => upd({ status: e.target.value })} className={`${INPUT} cursor-pointer`}>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </Field>
              ) : (
                <Field label="Category">
                  <input value={formData.category} onChange={e => upd({ category: e.target.value })}
                    placeholder="e.g. Follow-up, Booking" className={INPUT} />
                </Field>
              )}
            </div>

            {isEdit && (
              <Field label="Category">
                <input value={formData.category} onChange={e => upd({ category: e.target.value })}
                  placeholder="e.g. Follow-up, Booking" className={INPUT} />
              </Field>
            )}

            <Field label="Due Date">
              <input type="datetime-local" value={formData.due_date} onChange={e => upd({ due_date: e.target.value })} className={INPUT} />
            </Field>

            <Field label="Tags">
              <input value={formData.tags} onChange={e => upd({ tags: e.target.value })}
                placeholder="Comma-separated tags (e.g. payment, urgent, visa)" className={INPUT} />
            </Field>

            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={formData.reminder_enabled}
                onChange={e => upd({ reminder_enabled: e.target.checked })}
                className="w-4 h-4 rounded accent-[var(--accent)] cursor-pointer" />
              <span className="text-sm text-[var(--muted-foreground)]">Send email reminder before due date</span>
            </label>

            {formData.reminder_enabled && (
              <Field label="Remind me">
                <select value={formData.reminder_hours_before} onChange={e => upd({ reminder_hours_before: Number(e.target.value) })} className={`${INPUT} cursor-pointer`}>
                  <option value="1">1 hour before</option>
                  <option value="2">2 hours before</option>
                  <option value="4">4 hours before</option>
                  <option value="8">8 hours before</option>
                  <option value="12">12 hours before</option>
                  <option value="24">24 hours before (1 day)</option>
                  <option value="48">48 hours before (2 days)</option>
                  <option value="72">72 hours before (3 days)</option>
                  <option value="168">168 hours before (1 week)</option>
                </select>
              </Field>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-[var(--border)]">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-[var(--muted-foreground)] hover:bg-[var(--muted)] transition-all">
              Cancel
            </button>
            <button type="submit" disabled={!valid || loading}
              className="px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:brightness-105 disabled:opacity-50"
              style={{ backgroundColor: 'var(--primary)' }}>
              {loading ? (isEdit ? 'Updating…' : 'Creating…') : (isEdit ? 'Update Task' : 'Create Task')}
            </button>
          </div>
        </form>
      </div>

      <style>{`@keyframes taskModalFadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
};

export default TaskModal;
